import { Box, Dialog, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AppContext } from "../../context/AppContext";
import { OwnerConsoleViewController } from "../../controllers/OwnerConsoleViewController";
import { HomeownerMortgage, MortgageAddress } from "../../interfaces/AppDefinitions";
import MortgageForm, { MortgageFormValues } from "./MortgageForm";

// Mortgage Insights on the report: the equity panel, the "add your mortgage" form and when it offers
// itself. Agent sites embed the report in an iframe that grows to fit it, so a pop-up there could open
// off screen. When embedded, the form opens inside the equity panel (right under the home value)
// instead; opened directly, it is a dialog.

export const PROMPT_DELAY_MS = 8000;
const SEEN_KEY = "oc_mortgage_prompt_seen";

type FormState = { addrId: number; prompted: boolean } | null;

interface MortgageUI {
	enabled: boolean;
	embedded: boolean;
	address: MortgageAddress | null; // the property being viewed
	mortgage: HomeownerMortgage | null;
	form: FormState;
	justSaved: number | null; // address id saved in this visit, for a thank-you line
	openForm: () => void;
	closeForm: () => void;
	save: (values: MortgageFormValues) => Promise<void>;
}

const MortgageContext = createContext<MortgageUI | null>(null);

export function useMortgage() {
	return useContext(MortgageContext);
}

function seenThisVisit() {
	try {
		return sessionStorage.getItem(SEEN_KEY) === "1";
	} catch (e) {
		return false;
	}
}
function markSeen() {
	try {
		sessionStorage.setItem(SEEN_KEY, "1");
	} catch (e) {
		// Only stops the form offering itself twice in one visit.
	}
}

export function MortgageProvider(props: { children: ReactNode }) {
	const { appState, appDispatch } = useContext(AppContext);
	const { info, currentAddrId, enc, trackView, report_configuration } = appState;
	const insights = info?.mortgageInsights;
	const enabled = insights?.enabled === true;
	const embedded = typeof window !== "undefined" && window.parent !== window;
	const address = (currentAddrId !== undefined && insights?.addresses?.[String(currentAddrId)]) || null;
	const [form, setForm] = useState<FormState>(null);
	const [justSaved, setJustSaved] = useState<number | null>(null);
	const prompted = useRef(false);

	// Close the form when the homeowner switches to another property.
	useEffect(() => {
		setForm((f) => (f && f.addrId !== currentAddrId ? null : f));
	}, [currentAddrId]);

	// Offer the form on its own once per visit, when the server says this homeowner hasn't told us
	// (or it's out of date) and hasn't said "Not now" lately. Never in the agent's preview.
	useEffect(() => {
		if (!enabled || !trackView || prompted.current || currentAddrId === undefined) return;
		if (!insights?.prompt?.show || !insights.prompt.addrIds?.includes(currentAddrId)) return;
		if (seenThisVisit()) return;
		const offer = () => {
			if (prompted.current) return;
			prompted.current = true;
			markSeen();
			setForm({ addrId: currentAddrId, prompted: true });
			OwnerConsoleViewController.RecordMortgagePrompt({ enc, action: "shown", track: trackView });
		};
		if (embedded) {
			// Inside the panel there is nothing to interrupt, so it can show straight away.
			offer();
			return;
		}
		const timer = window.setTimeout(offer, PROMPT_DELAY_MS);
		const onScroll = () => {
			if (window.scrollY > 420) offer();
		};
		window.addEventListener("scroll", onScroll, { passive: true });
		return () => {
			window.clearTimeout(timer);
			window.removeEventListener("scroll", onScroll);
		};
	}, [enabled, trackView, currentAddrId, insights, embedded, enc]);

	const openForm = useCallback(() => {
		if (currentAddrId !== undefined) setForm({ addrId: currentAddrId, prompted: false });
	}, [currentAddrId]);

	const closeForm = useCallback(() => {
		if (form?.prompted) {
			OwnerConsoleViewController.RecordMortgagePrompt({ enc, action: "dismissed", track: trackView });
		}
		setForm(null);
	}, [form, enc, trackView]);

	const save = useCallback(
		async (values: MortgageFormValues) => {
			if (!form) return;
			const report = report_configuration.find((r) => r.addrId === form.addrId);
			const result = await OwnerConsoleViewController.SaveMortgage({
				...values,
				enc,
				addrId: form.addrId,
				track: trackView,
				agentName: info.agent?.agentName,
				estimatedValue: report?.reportData?.estimatedValue,
			});
			const key = String(form.addrId);
			const current = info.mortgageInsights ?? { enabled: true };
			const addresses = { ...(current.addresses ?? {}) };
			addresses[key] = {
				...(addresses[key] ?? { purchaseYear: null, purchasePrice: null }),
				mortgage: result.mortgage,
			};
			const prompt = current.prompt
				? { ...current.prompt, addrIds: current.prompt.addrIds.filter((id) => id !== form.addrId) }
				: current.prompt;
			appDispatch({
				type: "UPDATE_STATE",
				key: "info",
				value: { ...info, mortgageInsights: { ...current, addresses, prompt } },
			});
			setJustSaved(form.addrId);
			setForm(null);
		},
		[form, report_configuration, enc, trackView, info, appDispatch],
	);

	const value = useMemo<MortgageUI>(
		() => ({
			enabled,
			embedded,
			address,
			mortgage: address?.mortgage ?? null,
			form,
			justSaved,
			openForm,
			closeForm,
			save,
		}),
		[enabled, embedded, address, form, justSaved, openForm, closeForm, save],
	);

	return (
		<MortgageContext.Provider value={value}>
			{props.children}
			{enabled && !embedded && (
				<Dialog
					open={form !== null}
					onClose={closeForm}
					fullWidth
					maxWidth="xs"
					aria-labelledby="mortgage-form-title"
					sx={{
						"& .MuiDialog-container": { alignItems: { xs: "flex-end", sm: "center" } },
						"& .MuiDialog-paper": {
							m: { xs: 0, sm: 4 },
							width: { xs: "100%", sm: "calc(100% - 64px)" },
							maxHeight: { xs: "92vh", sm: "calc(100% - 64px)" },
							borderRadius: { xs: "20px 20px 0 0", sm: "18px" },
						},
					}}
				>
					<Box sx={{ position: "relative", p: { xs: "18px 18px 22px", sm: "24px" } }}>
						<IconButton
							aria-label="Close"
							onClick={closeForm}
							size="small"
							sx={{ position: "absolute", top: 10, right: 10 }}
						>
							<Close fontSize="small" />
						</IconButton>
						{form && (
							<MortgageForm
								initial={address?.mortgage ?? null}
								agentName={info.agent?.agentName}
								cancelLabel={form.prompted ? "Not now" : "Cancel"}
								onCancel={closeForm}
								onSubmit={save}
							/>
						)}
					</Box>
				</Dialog>
			)}
		</MortgageContext.Provider>
	);
}
